import { useState } from 'react'
import {
  createListingOnBehalf,
  type ManagedListingCreatePayload,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

interface ManagedListingFormProps {
  shopId: string
  onCreated?: (listingId: string, slugToken: string) => void
}

const CONDITIONS = ['new', 'used', 'refurbished']
const DELIVERY_TYPES = ['shipping', 'pickup', 'meetup', 'pickup-and-shipping']
const PRODUCTION_TYPES = ['handmade', 'resale', 'vintage', 'manufactured']

export function ManagedListingForm({ shopId, onCreated }: ManagedListingFormProps) {
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [condition, setCondition] = useState('used')
  const [deliveryType, setDeliveryType] = useState('shipping')
  const [productionType, setProductionType] = useState('vintage')
  const [floorDollars, setFloorDollars] = useState('')
  const [brand, setBrand] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastCreated, setLastCreated] = useState<{
    name: string
    slug_token: string
  } | null>(null)

  const canCreate = usePermission('listings', 'create_on_behalf')

  if (!canCreate) {
    return (
      <div className="rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4 text-sm text-gray-600 dark:text-gray-300">
        You do not have permission to create listings on behalf of a shop.
      </div>
    )
  }

  const reset = () => {
    setName('')
    setPrice('')
    setDescription('')
    setFloorDollars('')
    setBrand('')
    setError(null)
  }

  const handleSubmit = async () => {
    setError(null)
    if (!name.trim() || !price.trim() || !category.trim()) {
      setError('Name, price, and category are required.')
      return
    }
    const priceNum = Number.parseFloat(price)
    if (Number.isNaN(priceNum) || priceNum < 0) {
      setError('Price must be a positive number.')
      return
    }
    const payload: ManagedListingCreatePayload = {
      name: name.trim(),
      price: priceNum,
      category: category.trim(),
      condition,
      delivery_type: deliveryType,
      production_type: productionType,
      currency: 'USD',
      inventory_count: 1,
    }
    if (description.trim()) payload.description = description.trim()
    if (brand.trim()) payload.brand = brand.trim()
    if (floorDollars.trim() !== '') {
      const floorNum = Number.parseFloat(floorDollars)
      if (Number.isNaN(floorNum) || floorNum < 0) {
        setError('Floor must be a positive number in dollars.')
        return
      }
      payload.floor_price_cents = Math.round(floorNum * 100)
    }

    setSubmitting(true)
    const res = await createListingOnBehalf(shopId, payload)
    setSubmitting(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to create listing.')
      return
    }
    if (res.data) {
      setLastCreated({ name: payload.name, slug_token: res.data.slug_token })
      onCreated?.(res.data.listing_id, res.data.slug_token)
    }
    reset()
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Create listing on behalf</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Creates a text-only draft under this shop. Images are added separately
          after creation. If the shop has a negotiated fee schedule with a floor,
          the floor is enforced before the listing is persisted.
        </p>
      </div>

      {lastCreated && (
        <div className="rounded-xl bg-green-50 dark:bg-green-900/30 p-3 text-sm text-green-700 dark:text-green-300">
          Created: <span className="font-medium">{lastCreated.name}</span>{' '}
          (slug: <span className="font-mono text-xs">{lastCreated.slug_token}</span>)
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <CustomTextInput
            label="Item name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., 1970s Vintage Sailor Jacket"
          />
        </div>
        <CustomTextInput
          label="Price ($)"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="125.00"
        />
        <CustomTextInput
          label="Floor — shop net ($)"
          value={floorDollars}
          onChange={(e) => setFloorDollars(e.target.value)}
          placeholder="Optional, e.g., 40"
        />
        <CustomTextInput
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="e.g., outerwear"
        />
        <CustomTextInput
          label="Brand"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          placeholder="Optional"
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
            Condition
          </label>
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
            Production type
          </label>
          <select
            value={productionType}
            onChange={(e) => setProductionType(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {PRODUCTION_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
            Delivery type
          </label>
          <select
            value={deliveryType}
            onChange={(e) => setDeliveryType(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {DELIVERY_TYPES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Optional"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div>
        <CustomButton
          onClick={handleSubmit}
          disabled={submitting}
          variant="primary"
        >
          {submitting ? 'Creating...' : 'Create draft listing'}
        </CustomButton>
      </div>
    </div>
  )
}
